import React from "react";
import { Alert, Badge, Button, Spinner } from "../../../design-system";
import { PREVIEWABLE_MIME_TYPES } from "../../../helpers/internal_documentation_helper";
import { STATUS_LABEL, STATUS_TONE } from "../constants";
import { formatBytes, formatDate, formatRelativeTime, levelLabel } from "../helpers";

const SectionTitle = ({ children }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: "var(--kr-fg-muted)",
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const Pair = ({ label, value, mono = false }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: "1px solid var(--kr-border-subtle)" }}>
    <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{label}</span>
    <span
      style={{
        fontSize: 13,
        color: "var(--kr-fg-strong)",
        textAlign: "right",
        fontFamily: mono ? "var(--kr-font-mono, ui-monospace, monospace)" : "inherit",
      }}
    >
      {value}
    </span>
  </div>
);

export function DocumentDetail({
  doc,
  previewUrl,
  previewLoading,
  previewError,
  onAcknowledge,
  acknowledging = false,
  ackList = null,
  ackListLoading = false,
  ackListError = null,
  isManager = false,
  onOpenVersion = null,
}) {
  if (!doc) return null;
  const canPreview = PREVIEWABLE_MIME_TYPES.has(doc.mime_type);
  const requiresAck = doc.requires_acknowledgement === true;
  const myAck = doc.my_acknowledgement || null;
  const history = Array.isArray(doc.version_history) ? doc.version_history : [];
  const hasHistory = history.length > 1;
  // Si alguien (probablemente este mismo doc o uno de sus hermanos del
  // linaje) tiene un draft con version > la vigente, lo destacamos como
  // "versión pendiente de publicar".
  const pendingDraft = history.find(
    (h) => h.status === "draft" && h.version > (doc.version || 0),
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <SectionTitle>Información</SectionTitle>
        <div style={{ background: "var(--kr-bg-subtle)", borderRadius: 8, padding: "8px 14px" }}>
          <Pair label="Código" value={doc.code} mono />
          <Pair label="Título" value={doc.title} />
          <Pair label="Estado" value={<Badge tone={STATUS_TONE[doc.status] || "neutral"}>{STATUS_LABEL[doc.status] || doc.status}</Badge>} />
          <Pair
            label="Versión"
            value={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                v{doc.version || 1}
                {doc.is_current ? (
                  <Badge tone="success">Vigente</Badge>
                ) : (
                  <Badge tone="neutral">Histórica</Badge>
                )}
              </span>
            }
          />
          {requiresAck && (
            <Pair
              label="Lectura"
              value={<Badge tone="warning">Lectura obligatoria</Badge>}
            />
          )}
          <Pair label="Nivel" value={levelLabel(doc.level)} />
          <Pair label="Tipo" value={doc.type?.name || "—"} />
          <Pair label="Entrada en vigor" value={formatDate(doc.effective_at)} />
          <Pair label="Publicado" value={doc.published_at ? formatRelativeTime(doc.published_at) : "—"} />
          {doc.archived_at && <Pair label="Archivado" value={formatRelativeTime(doc.archived_at)} />}
        </div>
      </div>

      {requiresAck && myAck && (
        <AcknowledgementWidget
          myAck={myAck}
          onAcknowledge={onAcknowledge}
          acknowledging={acknowledging}
        />
      )}

      {requiresAck && isManager && (
        <ManagerAcknowledgementsSection
          ackList={ackList}
          loading={ackListLoading}
          error={ackListError}
        />
      )}

      {isManager && pendingDraft && (
        <Alert tone="info">
          Hay una <strong>versión {pendingDraft.version} en borrador</strong> pendiente
          de publicar.{" "}
          {onOpenVersion ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenVersion(pendingDraft.id)}
            >
              Abrir borrador
            </Button>
          ) : null}
        </Alert>
      )}

      {hasHistory && (
        <VersionHistorySection
          history={history}
          currentId={doc.id}
          onOpenVersion={onOpenVersion}
          isManager={isManager}
        />
      )}

      {doc.description && (
        <div>
          <SectionTitle>Descripción</SectionTitle>
          <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", whiteSpace: "pre-wrap" }}>
            {doc.description}
          </div>
        </div>
      )}

      <div>
        <SectionTitle>Archivo</SectionTitle>
        <div style={{ background: "var(--kr-bg-subtle)", borderRadius: 8, padding: "8px 14px" }}>
          <Pair label="Nombre" value={doc.original_filename} />
          <Pair label="Tamaño" value={formatBytes(doc.size_bytes)} />
          <Pair label="MIME" value={doc.mime_type} mono />
        </div>
      </div>

      <div>
        <SectionTitle>Previsualización</SectionTitle>
        {!canPreview ? (
          <div style={{ fontSize: 13, color: "var(--kr-fg-muted)" }}>
            Este formato no se puede previsualizar. Descárgalo para verlo.
          </div>
        ) : previewLoading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spinner size={16} />
          </div>
        ) : previewError ? (
          <div style={{ fontSize: 13, color: "var(--kr-danger-700)" }}>{previewError}</div>
        ) : previewUrl ? (
          doc.mime_type === "application/pdf" ? (
            <iframe
              title={doc.title}
              src={previewUrl}
              style={{
                width: "100%",
                height: 480,
                border: "1px solid var(--kr-border)",
                borderRadius: 8,
                background: "#fff",
              }}
            />
          ) : (
            <img
              alt={doc.title}
              src={previewUrl}
              style={{
                width: "100%",
                maxHeight: 480,
                objectFit: "contain",
                borderRadius: 8,
                border: "1px solid var(--kr-border)",
                background: "var(--kr-bg-subtle)",
              }}
            />
          )
        ) : null}
      </div>
    </div>
  );
}

function AcknowledgementWidget({ myAck, onAcknowledge, acknowledging }) {
  if (myAck.status === "done") {
    return (
      <Alert tone="success">
        Marcaste este documento como leído
        {myAck.acknowledged_at ? ` · ${formatRelativeTime(myAck.acknowledged_at)}` : ""}.
      </Alert>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        padding: 14,
        border: "1px solid var(--kr-border)",
        borderRadius: 8,
        background: "var(--kr-bg-subtle)",
      }}
    >
      <div style={{ flex: 1, fontSize: 13, color: "var(--kr-fg-strong)" }}>
        Tienes este documento como <strong>pendiente de leer</strong>. Marca el
        acuse cuando lo hayas revisado.
      </div>
      <Button
        variant="primary"
        icon="Check"
        onClick={onAcknowledge}
        disabled={acknowledging || !onAcknowledge}
      >
        {acknowledging ? "Guardando…" : "Marcar como leído"}
      </Button>
    </div>
  );
}

function ManagerAcknowledgementsSection({ ackList, loading, error }) {
  const summary = ackList?.summary;
  const rows = ackList?.rows || [];

  return (
    <div>
      <SectionTitle>
        Acuses de lectura
        {summary && (
          <span style={{ marginLeft: 8, color: "var(--kr-fg-muted)", textTransform: "none", letterSpacing: 0 }}>
            {summary.done}/{summary.total} leídos · {summary.pending} pendientes
          </span>
        )}
      </SectionTitle>
      {loading ? (
        <div style={{ padding: 16, textAlign: "center" }}>
          <Spinner size={16} />
        </div>
      ) : error ? (
        <Alert tone="danger">{error}</Alert>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--kr-fg-muted)" }}>
          Aún no hay empleados asignados a este documento.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid var(--kr-border)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {rows.map((r, idx) => (
            <div
              key={r.employee_profile_id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                borderTop: idx === 0 ? "none" : "1px solid var(--kr-border-subtle)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--kr-fg-strong)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.employee_name || "—"}
                </div>
                {r.position && (
                  <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
                    {r.position}
                  </div>
                )}
              </div>
              {r.status === "done" ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                  <Badge tone="success">Leído</Badge>
                  {r.acknowledged_at && (
                    <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
                      {formatRelativeTime(r.acknowledged_at)}
                    </span>
                  )}
                </div>
              ) : (
                <Badge tone="warning">Pendiente</Badge>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VersionHistorySection({ history, currentId, onOpenVersion, isManager }) {
  // Mostramos la más reciente primero.
  const sorted = [...history].sort((a, b) => b.version - a.version);
  return (
    <div>
      <SectionTitle>Historial de versiones</SectionTitle>
      <div
        style={{
          border: "1px solid var(--kr-border)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {sorted.map((v, idx) => {
          const isOpen = v.id === currentId;
          const clickable = !isOpen && (isManager || v.status === "published");
          return (
            <div
              key={v.id}
              onClick={clickable && onOpenVersion ? () => onOpenVersion(v.id) : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                borderTop: idx === 0 ? "none" : "1px solid var(--kr-border-subtle)",
                background: isOpen ? "var(--kr-bg-subtle)" : "#fff",
                cursor: clickable ? "pointer" : "default",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 56 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: "var(--kr-fg-strong)" }}>
                  v{v.version}
                </span>
                {v.is_current ? (
                  <Badge tone="success">Vigente</Badge>
                ) : v.status === "draft" ? (
                  <Badge tone="warning">Borrador</Badge>
                ) : (
                  <Badge tone="neutral">Histórica</Badge>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--kr-fg-strong)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {v.original_filename}
                </div>
                {v.version_notes && (
                  <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
                    {v.version_notes}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", textAlign: "right", whiteSpace: "nowrap" }}>
                {v.published_at
                  ? formatRelativeTime(v.published_at)
                  : v.status === "draft"
                    ? "Sin publicar"
                    : "—"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
